import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  ColorInput,
  Divider,
  Group,
  NumberInput,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import {
  IconFilter,
  IconPencil,
  IconPlayerPlay,
  IconPlus,
  IconRefresh,
  IconStar,
  IconTrash,
} from '@/components/icons'
import {
  AppModal,
  EmptyState,
  PageCenterLoader,
  PageShell,
  PageToolbar,
} from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import { notifyProjectsChanged } from '@/lib/projects-events'
import {
  mergeWithDefaultPermissions,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import {
  SAVED_FILTER_PRIORITY_OPTIONS,
  SAVED_FILTER_STATUS_OPTIONS,
  emptySavedFilterForm,
  savedFilterCriteriaFromForm,
  savedFilterCriteriaSummaryLines,
  savedFilterFormFromRow,
  savedFilterMatchesQuery,
  savedFilterWorkspaceUserLabel,
  type SavedFilterFormState,
} from '@/lib/saved-filters'
import { buildSavedFilterSearch } from '@/lib/tickets-nav'
import type { MyPermissionsResponse, ProjectRow, SavedFilterRow, WorkspaceUserRow } from '@/types/api'

export function FiltersPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])
  const effectivePerm = mergeWithDefaultPermissions(perm, user?.role)
  const canAccess = hasPermission(effectivePerm, 'access_tickets')

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [filters, setFilters] = useState<SavedFilterRow[]>([])
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [workspaceUsers, setWorkspaceUsers] = useState<WorkspaceUserRow[]>([])
  const [search, setSearch] = useState('')

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [projects],
  )

  const visibleFilters = useMemo(() => {
    return filters.filter((f) => savedFilterMatchesQuery(f, search, projects, workspaceUsers))
  }, [filters, search, projects, workspaceUsers])

  const loadAll = useCallback(async () => {
    if (!canAccess) {
      setLoading(false)
      return
    }
    setLoading(true)
    setLoadError(null)
    try {
      const [fl, pl, ul] = await Promise.all([
        api.filtersList(),
        api.projects(),
        api.workspaceUsers(),
      ])
      setFilters(Array.isArray(fl.filters) ? fl.filters : [])
      setProjects(Array.isArray(pl) ? pl : [])
      setWorkspaceUsers(Array.isArray(ul) ? ul : [])
    } catch (e) {
      setFilters([])
      setLoadError(e instanceof ApiError ? e.message : 'Не удалось загрузить фильтры')
    } finally {
      setLoading(false)
    }
  }, [canAccess])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<SavedFilterFormState>(() => emptySavedFilterForm())
  const [saving, setSaving] = useState(false)

  const openCreate = () => {
    setEditingId(null)
    setForm(emptySavedFilterForm())
    setModalOpen(true)
  }

  const openEdit = (f: SavedFilterRow) => {
    if (f.is_owner === false) return
    setEditingId(f.id)
    setForm(savedFilterFormFromRow(f))
    setModalOpen(true)
  }

  const applyFilter = (f: SavedFilterRow) => {
    const qs = buildSavedFilterSearch(f, projects)
    navigate(`/tickets?${qs}`)
  }

  const onDelete = (f: SavedFilterRow) => {
    if (f.is_owner === false) return
    openConfirmModal({
      title: 'Удалить фильтр?',
      children: (
        <Text size="sm" c="dimmed">
          «{f.name}» будет удалён без восстановления.
        </Text>
      ),
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.filterDelete(f.id)
          showNotification({ title: 'Удалено', message: f.name, color: 'green' })
          notifyProjectsChanged()
          void loadAll()
        } catch (e) {
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Не удалось удалить',
            color: 'red',
          })
        }
      },
    })
  }

  const onSubmitModal = async () => {
    const name = form.name.trim()
    if (!name) {
      showNotification({ title: 'Форма', message: 'Укажите название фильтра', color: 'yellow' })
      return
    }
    const criteria = savedFilterCriteriaFromForm(form)
    setSaving(true)
    try {
      if (editingId) {
        await api.filterUpdate(editingId, {
          name,
          description: form.description.trim() || null,
          color: form.color,
          is_favorite: form.is_favorite,
          available_to_all: form.available_to_all,
          criteria,
        })
        showNotification({ title: 'Сохранено', message: name, color: 'green' })
      } else {
        await api.filterCreate({
          name,
          description: form.description.trim() || null,
          color: form.color,
          is_favorite: form.is_favorite,
          available_to_all: form.available_to_all,
          criteria,
        })
        showNotification({ title: 'Создано', message: name, color: 'green' })
      }
      setModalOpen(false)
      notifyProjectsChanged()
      void loadAll()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const toggleIn = (
    field: 'statuses' | 'priorities' | 'projectIds' | 'assignedUserIds',
    value: string,
  ) => {
    setForm((prev) => {
      const arr = prev[field]
      const has = arr.includes(value)
      const next = has ? arr.filter((x) => x !== value) : [...arr, value]
      return { ...prev, [field]: next }
    })
  }

  if (!canAccess) {
    return (
      <PageShell>
        <Alert color="gray" variant="light" title="Нет доступа">
          Раздел доступен при праве на обращения.
        </Alert>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          align="flex-start"
          leading={
            <Box>
              <Group gap="xs" wrap="nowrap">
                <IconFilter size={20} stroke={1.6} />
                <Text fw={700} size="lg">
                  Сохранённые фильтры
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Наборы условий для быстрого открытия нужных списков обращений.
              </Text>
            </Box>
          }
          trailing={
            <Group gap="xs" wrap="wrap" justify="flex-end">
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} stroke={1.5} />}
                onClick={() => void loadAll()}
                disabled={loading}
              >
                Обновить
              </Button>
              <Button size="xs" leftSection={<IconPlus size={14} stroke={1.5} />} onClick={openCreate}>
                Создать фильтр
              </Button>
            </Group>
          }
        />

        {loadError ? (
          <Alert color="red" variant="light" title="Ошибка загрузки">
            {loadError}
          </Alert>
        ) : null}

        {!loading ? (
          <Card withBorder padding="sm" radius="md">
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
              <TextInput
                label="Поиск"
                placeholder="Название, описание или условия…"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                leftSection={<IconFilter size={16} stroke={1.5} style={{ opacity: 0.55 }} />}
                style={{ flex: '1 1 260px', minWidth: 200 }}
              />
              <Text size="xs" c="dimmed" style={{ alignSelf: 'center' }}>
                Всего: {filters.length}
                {search.trim() ? ` · показано: ${visibleFilters.length}` : ''}
              </Text>
            </Group>
          </Card>
        ) : null}

        {loading ? (
          <PageCenterLoader label="Загрузка фильтров" minHeight={200} />
        ) : filters.length === 0 ? (
          <EmptyState
            title="Нет сохранённых фильтров"
            description="Создайте фильтр, чтобы одним кликом открывать список обращений с нужными статусами, проектами и назначением."
            icon={<IconFilter size={32} stroke={1.5} />}
            action={
              <Button size="xs" leftSection={<IconPlus size={14} stroke={1.5} />} onClick={openCreate}>
                Создать фильтр
              </Button>
            }
          />
        ) : visibleFilters.length === 0 ? (
          <EmptyState
            title="Ничего не найдено"
            description="Измените запрос в поле поиска."
            icon={<IconFilter size={32} stroke={1.5} />}
          />
        ) : (
          <Stack gap="md">
            {visibleFilters.map((f) => {
              const lines = savedFilterCriteriaSummaryLines(f.criteria, projects, workspaceUsers)
              const isOwner = f.is_owner !== false
              const accent = f.color || 'var(--mantine-color-blue-filled)'
              return (
                <Card
                  key={f.id}
                  withBorder
                  padding="md"
                  radius="md"
                  style={{
                    borderLeftWidth: 4,
                    borderLeftStyle: 'solid',
                    borderLeftColor: accent,
                  }}
                >
                  <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm" mb="sm">
                    <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                      <Group gap="xs" wrap="wrap" align="center">
                        <Text fw={600} size="sm" lineClamp={2}>
                          {f.name}
                        </Text>
                        {f.available_to_all ? (
                          <Badge size="xs" variant="light" color="gray">
                            Общий
                          </Badge>
                        ) : null}
                        {f.is_favorite ? (
                          <Badge
                            size="xs"
                            variant="light"
                            color="yellow"
                            leftSection={<IconStar size={10} stroke={1.5} />}
                          >
                            В меню
                          </Badge>
                        ) : null}
                      </Group>
                      {f.description ? (
                        <Text size="xs" c="dimmed" lineClamp={3}>
                          {f.description}
                        </Text>
                      ) : null}
                    </Stack>
                    <Group gap={4} wrap="nowrap">
                      {isOwner ? (
                        <>
                          <ActionIcon
                            variant="default"
                            size="sm"
                            aria-label="Изменить фильтр"
                            onClick={() => openEdit(f)}
                          >
                            <IconPencil size={16} stroke={1.5} />
                          </ActionIcon>
                          <ActionIcon
                            variant="default"
                            size="sm"
                            color="red"
                            aria-label="Удалить фильтр"
                            onClick={() => onDelete(f)}
                          >
                            <IconTrash size={16} stroke={1.5} />
                          </ActionIcon>
                        </>
                      ) : null}
                      <Button
                        size="compact-xs"
                        leftSection={<IconPlayerPlay size={14} stroke={1.5} />}
                        onClick={() => applyFilter(f)}
                      >
                        Применить
                      </Button>
                    </Group>
                  </Group>
                  <Divider my="sm" />
                  <Stack gap={6}>
                    {lines.length === 0 ? (
                      <Text size="xs" c="dimmed">
                        Условия не заданы — список откроется с настройками по умолчанию.
                      </Text>
                    ) : (
                      lines.map((line) => (
                        <Text key={line} size="xs" c="dimmed" lh={1.45}>
                          · {line}
                        </Text>
                      ))
                    )}
                  </Stack>
                </Card>
              )
            })}
          </Stack>
        )}
      </Stack>

      <AppModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Редактировать фильтр' : 'Новый фильтр'}
        size="lg"
        scrollAreaComponent={ScrollArea.Autosize}
        footer={
          <>
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Отмена
            </Button>
            <Button loading={saving} onClick={() => void onSubmitModal()}>
              {editingId ? 'Сохранить' : 'Создать'}
            </Button>
          </>
        }
      >
        <Stack gap="md">
          <TextInput
            label="Название"
            placeholder="Например, Мои срочные"
            required
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.currentTarget.value }))}
          />
          <Textarea
            label="Описание"
            placeholder="Необязательно"
            minRows={2}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.currentTarget.value }))}
          />
          <ColorInput
            label="Цвет в списке и в боковом меню"
            format="hex"
            value={form.color}
            onChange={(c) => setForm((p) => ({ ...p, color: c }))}
          />

          <Divider label="Условия списка" labelPosition="left" />
          <Text size="xs" c="dimmed">
            Неотмеченные поля к фильтрации не применяются.
          </Text>

          <Box>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" lts={0.04} mb={8}>
              Статус
            </Text>
            <Group gap="md">
              {SAVED_FILTER_STATUS_OPTIONS.map((o) => (
                <Checkbox
                  key={o.value}
                  label={o.label}
                  checked={form.statuses.includes(o.value)}
                  onChange={() => toggleIn('statuses', o.value)}
                />
              ))}
            </Group>
          </Box>

          <Box>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" lts={0.04} mb={8}>
              Приоритет
            </Text>
            <Group gap="md">
              {SAVED_FILTER_PRIORITY_OPTIONS.map((o) => (
                <Checkbox
                  key={o.value}
                  label={o.label}
                  checked={form.priorities.includes(o.value)}
                  onChange={() => toggleIn('priorities', o.value)}
                />
              ))}
            </Group>
          </Box>

          <Box>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" lts={0.04} mb={8}>
              Проекты
            </Text>
            <ScrollArea.Autosize mah={200} type="auto" offsetScrollbars>
              <Stack gap={6}>
                {sortedProjects.length === 0 ? (
                  <Text size="xs" c="dimmed">
                    Нет проектов
                  </Text>
                ) : (
                  sortedProjects.map((p) => (
                    <Checkbox
                      key={p.id}
                      label={p.name || p.slug || p.id}
                      checked={form.projectIds.includes(p.id)}
                      onChange={() => toggleIn('projectIds', p.id)}
                    />
                  ))
                )}
              </Stack>
            </ScrollArea.Autosize>
          </Box>

          <Box>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" lts={0.04} mb={8}>
              Назначено на
            </Text>
            <ScrollArea.Autosize mah={220} type="auto" offsetScrollbars>
              <Stack gap={6}>
                <Checkbox
                  label="Не назначено"
                  checked={form.unassigned}
                  onChange={(e) => setForm((p) => ({ ...p, unassigned: e.currentTarget.checked }))}
                />
                {workspaceUsers.map((u) => (
                  <Checkbox
                    key={u.id}
                    label={savedFilterWorkspaceUserLabel(u)}
                    checked={form.assignedUserIds.includes(u.id)}
                    onChange={() => toggleIn('assignedUserIds', u.id)}
                  />
                ))}
              </Stack>
            </ScrollArea.Autosize>
          </Box>

          <Group grow>
            <NumberInput
              label="Создано за последние (ч)"
              placeholder="Любое"
              min={1}
              hideControls
              value={form.created_within_hours === '' ? undefined : Number(form.created_within_hours)}
              onChange={(v) =>
                setForm((p) => ({
                  ...p,
                  created_within_hours: v === '' || v === undefined ? '' : String(v),
                }))
              }
            />
            <NumberInput
              label="Обновлено за последние (ч)"
              placeholder="Любое"
              min={1}
              hideControls
              value={form.updated_within_hours === '' ? undefined : Number(form.updated_within_hours)}
              onChange={(v) =>
                setForm((p) => ({
                  ...p,
                  updated_within_hours: v === '' || v === undefined ? '' : String(v),
                }))
              }
            />
          </Group>

          <Divider label="Доступ" labelPosition="left" />

          <Checkbox
            label="Показывать в боковом меню (избранное)"
            checked={form.is_favorite}
            onChange={(e) => setForm((p) => ({ ...p, is_favorite: e.currentTarget.checked }))}
          />
          <Checkbox
            label="Доступен всем операторам workspace"
            checked={form.available_to_all}
            onChange={(e) => setForm((p) => ({ ...p, available_to_all: e.currentTarget.checked }))}
          />
          <Text size="xs" c="dimmed">
            Общий фильтр виден всем, редактировать может только автор.
          </Text>
        </Stack>
      </AppModal>
    </PageShell>
  )
}
